import React, { useContext, useEffect, useState } from 'react'
import { GlobalContext, TenantSettingsContext } from '../../../App'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import API from '../../../services/API'
import { Autocomplete, Divider, Switch, TextField } from '@mui/material'
import ApplicationLinkCard from './ApplicationLinkCard'

const Applications = () => {
  const [enableEmployeeApplications, setEnableEmployeeApplications] =
    useState(false)
  const [restrictApplicationsByRole, setRestrictApplicationsByRole] = useState(
    [],
  )
  const [roleOptions, setRoleOptions] = useState([])
  const [automaticReferences, setAutomaticReferences] = useState(true)
  const [applicationDraftReminders, setApplicationDraftReminders] =
    useState(true)
  const [onboardingTasks, setOnboardingTasks] = useState([])

  useEffect(() => {
    API.get(
      '/settings?select=restrictApplicationsByRole%20enableEmployeeApplications%20roles%20automaticReferences%20applicationDraftReminders%20onboardingTasks',
    ).then((res) => {
      setRestrictApplicationsByRole(res.data?.restrictApplicationsByRole ?? [])
      setEnableEmployeeApplications(!!res.data?.enableEmployeeApplications)
      setRoleOptions(res.data?.roles ?? [])
      setAutomaticReferences(res.data?.automaticReferences ?? true)
      setApplicationDraftReminders(res.data?.applicationDraftReminders ?? true)
      setOnboardingTasks(res.data?.onboardingTasks ?? [])
    })
  }, [])

  const { setToast } = useContext(GlobalContext)

  const handleSaveChanges = (e) => {
    e.preventDefault()
    API.put('/site-config', {
      enableEmployeeApplications,
      restrictApplicationsByRole,
      automaticReferences,
      applicationDraftReminders,
      onboardingTasks,
    }).then((res) => {
      if (res.status === 200) {
        setToast(true)
      }
    })
  }

  return (
    <Stack spacing={3} component="form" onSubmit={handleSaveChanges}>
      <Typography variant="title2">Employee Applications</Typography>
      <ApplicationLinkCard applicationsEnabled={enableEmployeeApplications} />
      <Stack direction="row" alignItems="center" gap={1}>
        <Switch
          checked={enableEmployeeApplications}
          onChange={(e) => setEnableEmployeeApplications(e.target.checked)}
          color="success"
        />
        <Stack>
          <Typography>Enable new employee applications</Typography>
          <Typography variant="caption">
            If disabled, new applications will be prevented. Applicants that
            have already started an application will be permitted to submit
            their application.
          </Typography>
        </Stack>
      </Stack>
      <Autocomplete
        title="Restrict Applications by Roles"
        value={restrictApplicationsByRole}
        options={roleOptions}
        multiple
        onChange={(e, newValue) => setRestrictApplicationsByRole(newValue)}
        renderInput={(props) => (
          <TextField
            {...props}
            label="Restrict Applications By Role"
            helperText="Only permit new applications from these roles"
          />
        )}
        disabled={!!!enableEmployeeApplications}
      />
      <Stack direction="row" alignItems="center" gap={1}>
        <Switch
          checked={automaticReferences}
          onChange={(e) => setAutomaticReferences(e.target.checked)}
          color="success"
        />
        <Stack>
          <Typography>
            Automatically send reference request emails once an applicant
            submits their application
          </Typography>
          <Typography variant="caption">
            If deselected, you will need to manually request a reference from
            within the application pages
          </Typography>
        </Stack>
      </Stack>
      <Stack direction="row" alignItems="center" gap={1}>
        <Switch
          checked={applicationDraftReminders}
          onChange={(e) => setApplicationDraftReminders(e.target.checked)}
          color="success"
        />
        <Stack>
          <Typography>
            Remind applicants to finish draft applications
          </Typography>
          <Typography variant="caption">
            Applicants whose application hasn't been touched for 48 hours are
            emailed a reminder to complete and submit it, every 48 hours (up
            to 10 reminders). Submitted applications are never reminded.
          </Typography>
        </Stack>
      </Stack>
      <Divider />
      <Stack gap={0.5}>
        <Typography variant="title3">Onboarding Checklist</Typography>
        <Typography variant="caption">
          When an application is accepted, these tasks are added to the new
          starter's onboarding checklist on their employee page.
        </Typography>
      </Stack>
      <Autocomplete
        multiple
        freeSolo
        value={onboardingTasks}
        options={[]}
        onChange={(e, newValue) => setOnboardingTasks(newValue)}
        renderInput={(props) => (
          <TextField
            {...props}
            label="Onboarding Tasks"
            helperText="Type a task and press Enter to add it, e.g. Issue uniform, Complete induction training, Set up payroll"
          />
        )}
      />
      <Button
        color="success"
        type="submit"
        variant="contained"
        size="small"
        fullWidth
      >
        Save Changes
      </Button>
    </Stack>
  )
}

export default Applications
